/**
 * Top Picks Page
 *
 * Premium feature with two main tabs:
 * 1. Top Picks - AI-curated betting picks with detailed analysis
 * 2. All Picks (Edge Finder) - Browse all predictions vs sportsbook lines
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PremiumGateModal from '../components/common/PremiumGateModal';
import TabBar from '../components/common/TabBar';
import TopPickCard from '../components/top-picks/TopPickCard';
import SuggestedParlays from '../components/edge-finder/SuggestedParlays';
import FeaturedPickLab from '../components/edge-finder/FeaturedPickLab';
import NeedlesBoard from '../components/edge-finder/NeedlesBoard';
import StreakBoard from '../components/streaks/StreakBoard';
import api from '../services/api';
import useActiveSports from '../hooks/useActiveSports';

// Main tabs
const MAIN_TABS = [
  { id: 'top-picks', label: 'Top Picks' },
  { id: 'edges', label: 'Edges' },
  { id: 'streak', label: 'Streak' },
];

// Sports each tab can show. Only the ones in season (sports_seasons) get a
// button - see useActiveSports.
const TOP_PICK_SPORTS = ['nfl', 'ncaa_f', 'nba', 'ncaa_b', 'nhl', 'mlb'];
const STREAK_SPORTS = ['nba', 'nhl', 'nfl', 'mlb']; // sports with player props
const NEEDLE_SPORTS = ['nba', 'nhl'];

// Pick type filter options
const PICK_TYPES = [
  { id: 'all', label: 'All Types' },
  { id: 'player_prop', label: 'Player Props' },
  { id: 'game', label: 'Game Picks' },
];

/**
 * Get the effective picks date.
 * Shows previous day's picks until 9am EST the next morning.
 * This ensures users can see picks for evening games through the night.
 */
function getPicksDate() {
  const now = new Date();

  // Get current time in EST
  const estOptions = { timeZone: 'America/New_York' };
  const estHour = parseInt(now.toLocaleString('en-US', { ...estOptions, hour: 'numeric', hour12: false }), 10);

  // Get date parts in EST timezone
  const estYear = parseInt(now.toLocaleString('en-US', { ...estOptions, year: 'numeric' }), 10);
  const estMonth = parseInt(now.toLocaleString('en-US', { ...estOptions, month: 'numeric' }), 10);
  const estDay = parseInt(now.toLocaleString('en-US', { ...estOptions, day: 'numeric' }), 10);

  // If before 9am EST, use yesterday's date
  let year = estYear;
  let month = estMonth;
  let day = estDay;

  if (estHour < 9) {
    // Subtract one day
    const yesterday = new Date(estYear, estMonth - 1, estDay - 1);
    year = yesterday.getFullYear();
    month = yesterday.getMonth() + 1;
    day = yesterday.getDate();
  }

  // Format as YYYY-MM-DD
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * EdgesContent - Parlay board: quality tiers, HR picks, pitcher combos +
 * small-multiples grid. Mounts SuggestedParlays for MLB. (Ride/Fade streak
 * picks moved to the new Streak tab.)
 */
function EdgesContent() {
  // NBA/NHL needles by default; the parlay board (daily_parlay_legs from
  // build_parlay.py) only has MLB legs, so it sat empty once MLB ended.
  const [sport, setSport] = useState('needles');
  const { sports: needleSports } = useActiveSports(NEEDLE_SPORTS);
  const { sports: parlaySports } = useActiveSports(['mlb']);
  const boards = [
    { id: 'needles', label: `${(needleSports.length ? needleSports : NEEDLE_SPORTS.map((id) => ({ label: id.toUpperCase() }))).map((s) => s.label).join(' & ')} Needles` },
    ...parlaySports.map((s) => ({ id: s.id, label: `${s.label} Parlays` })),
  ];
  const board = boards.some((b) => b.id === sport) ? sport : 'needles';
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-text-secondary">Board:</span>
        {boards.map((s) => (
          <button
            key={s.id}
            onClick={() => setSport(s.id)}
            className={
              'px-3 py-1 rounded text-sm transition ' +
              (board === s.id
                ? 'bg-accent-blue/20 text-accent-blue font-semibold'
                : 'text-text-secondary hover:text-text-primary')
            }
          >
            {s.label}
          </button>
        ))}
      </div>
      {board === 'needles' ? <NeedlesBoard /> : <SuggestedParlays sport={board} />}
    </div>
  );
}

/**
 * StreakContent - Ride/Fade recommendations based on streak analysis
 * Shows players on hot/cold streaks with RIDE (bet over) or FADE (bet under) signals
 */
function StreakContent() {
  const [props, setProps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { sports: streakSports } = useActiveSports(STREAK_SPORTS);
  const [pickedSport, setSelectedSport] = useState(null);
  // The user's pick, or the first in-season sport (NBA when in season).
  const selectedSport = streakSports.some((s) => s.id === pickedSport)
    ? pickedSport : (streakSports[0]?.id || 'nba');
  const [filterType, setFilterType] = useState('all'); // all, ride, fade

  useEffect(() => {
    const fetchEdges = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append('limit', '100');

        const response = await api.get(`/props/${selectedSport}?${params.toString()}`);
        const allProps = response.data?.props || [];

        // Filter to props with streak data and sportsbook lines
        const propsWithStreaks = allProps.filter(p =>
          p.streak_games_hit !== null &&
          p.streak_games_window !== null &&
          p.sb_line !== null &&
          p.streak_games_window >= 3
        );

        // Sort by streak strength (hit rate * games)
        propsWithStreaks.sort((a, b) => {
          const aStrength = (a.streak_hit_rate || 0) * (a.streak_games_window || 0);
          const bStrength = (b.streak_hit_rate || 0) * (b.streak_games_window || 0);
          return bStrength - aStrength;
        });

        setProps(propsWithStreaks);
      } catch (err) {
        console.error('Error fetching edges:', err);
        setError(err.response?.data?.detail || 'Failed to load edges');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEdges();
  }, [selectedSport]);

  // Determine RIDE or FADE recommendation
  const getRecommendation = (prop) => {
    const hitRate = prop.streak_hit_rate || 0;
    const continuationProb = prop.streak_continuation_prob || hitRate;

    // RIDE: High hit rate (60%+) suggests momentum - bet OVER
    // FADE: Low hit rate (<40%) suggests regression - bet UNDER
    if (continuationProb >= 0.60 || hitRate >= 0.60) {
      return { type: 'RIDE', color: 'green', direction: 'OVER', description: 'Hot streak - momentum play' };
    } else if (continuationProb <= 0.40 || hitRate <= 0.40) {
      return { type: 'FADE', color: 'red', direction: 'UNDER', description: 'Cold/regression expected' };
    }
    return { type: 'NEUTRAL', color: 'gray', direction: '-', description: 'No clear edge' };
  };

  // Filter props based on selection
  const filteredProps = props.filter(prop => {
    const rec = getRecommendation(prop);
    if (filterType === 'ride') return rec.type === 'RIDE';
    if (filterType === 'fade') return rec.type === 'FADE';
    return rec.type !== 'NEUTRAL'; // 'all' shows RIDE and FADE, not neutral
  });

  // Summary counts
  const rideCount = props.filter(p => getRecommendation(p).type === 'RIDE').length;
  const fadeCount = props.filter(p => getRecommendation(p).type === 'FADE').length;

  return (
    <div>
      {/* Today's Streak Board (the one posted to Instagram) */}
      <StreakBoard />

      {/* Description */}
      <div className="mb-4">
        <p className="text-text-secondary">
          Streak-based picks: <span className="text-green-400 font-semibold">RIDE</span> hot players (bet over) or{' '}
          <span className="text-red-400 font-semibold">FADE</span> cold players (bet under)
        </p>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Sport Filter */}
          <div className="flex-1">
            <label className="block text-xs text-text-secondary mb-2 uppercase tracking-wide">
              Sport
            </label>
            <div className="flex flex-wrap gap-2">
              {streakSports.map(sport => (
                <button
                  key={sport.id}
                  onClick={() => setSelectedSport(sport.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    selectedSport === sport.id
                      ? 'bg-accent-blue text-white'
                      : 'bg-bg-card hover:bg-bg-card-hover text-text-secondary'
                  }`}
                >
                  {sport.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex-1">
            <label className="block text-xs text-text-secondary mb-2 uppercase tracking-wide">
              Signal Type
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  filterType === 'all'
                    ? 'bg-accent-blue text-white'
                    : 'bg-bg-card hover:bg-bg-card-hover text-text-secondary'
                }`}
              >
                All ({rideCount + fadeCount})
              </button>
              <button
                onClick={() => setFilterType('ride')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  filterType === 'ride'
                    ? 'bg-green-600 text-white'
                    : 'bg-bg-card hover:bg-bg-card-hover text-green-400'
                }`}
              >
                RIDE ({rideCount})
              </button>
              <button
                onClick={() => setFilterType('fade')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  filterType === 'fade'
                    ? 'bg-red-600 text-white'
                    : 'bg-bg-card hover:bg-bg-card-hover text-red-400'
                }`}
              >
                FADE ({fadeCount})
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="glass-card p-6 animate-pulse">
              <div className="h-4 bg-bg-card rounded w-1/3 mb-4"></div>
              <div className="h-6 bg-bg-card rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-bg-card rounded w-1/2"></div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="glass-card bg-red-500/10 border-red-500/30 p-6 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredProps.length === 0 && (
        <div className="glass-card p-8 text-center">
          <div className="text-5xl mb-4">&#128293;</div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No edges found
          </h3>
          <p className="text-text-secondary">
            No strong ride/fade signals available for {selectedSport.toUpperCase()} today.
          </p>
        </div>
      )}

      {/* Edges Grid */}
      {!isLoading && !error && filteredProps.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProps.map(prop => {
            const rec = getRecommendation(prop);
            const hitRate = Math.round((prop.streak_hit_rate || 0) * 100);
            const streakText = `${prop.streak_games_hit}/${prop.streak_games_window} L${prop.streak_games_window}`;

            return (
              <div
                key={prop.id}
                className={`glass-card p-4 border-l-4 ${
                  rec.type === 'RIDE' ? 'border-l-green-500' : 'border-l-red-500'
                }`}
              >
                {/* Header with signal badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    rec.type === 'RIDE'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {rec.type} {rec.direction}
                  </span>
                  <span className="text-xs text-text-secondary uppercase">
                    {prop.sport?.toUpperCase()}
                  </span>
                </div>

                {/* Player name */}
                <h3 className="text-lg font-bold text-text-primary mb-1">
                  {prop.player_name}
                </h3>

                {/* Team and matchup */}
                <p className="text-sm text-text-secondary mb-3">
                  {prop.team} {prop.matchup && `- ${prop.matchup}`}
                </p>

                {/* Prop line */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl font-bold text-accent-blue">
                    {prop.sb_line}
                  </span>
                  <span className="text-sm text-text-secondary">
                    {prop.stat_type?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Streak info */}
                <div className="bg-bg-secondary rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-text-secondary uppercase">Streak</span>
                    <span className={`text-sm font-semibold ${
                      hitRate >= 60 ? 'text-green-400' : hitRate <= 40 ? 'text-red-400' : 'text-text-primary'
                    }`}>
                      {streakText} ({hitRate}%)
                    </span>
                  </div>

                  {/* Visual streak indicator */}
                  <div className="flex gap-1">
                    {Array.from({ length: prop.streak_games_window || 5 }).map((_, i) => (
                      <div
                        key={i}
                        className={`flex-1 h-2 rounded ${
                          i < (prop.streak_games_hit || 0)
                            ? rec.type === 'RIDE' ? 'bg-green-500' : 'bg-red-500'
                            : 'bg-bg-card'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Season average comparison */}
                  {prop.season_avg !== null && (
                    <div className="mt-2 text-xs text-text-secondary">
                      Season avg: {prop.season_avg?.toFixed(1)} | Line: {prop.sb_line}
                      {prop.season_avg > prop.sb_line && (
                        <span className="text-green-400 ml-1">(+{(prop.season_avg - prop.sb_line).toFixed(1)})</span>
                      )}
                      {prop.season_avg < prop.sb_line && (
                        <span className="text-red-400 ml-1">({(prop.season_avg - prop.sb_line).toFixed(1)})</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Recommendation note */}
                <p className="text-xs text-text-secondary mt-3 italic">
                  {rec.description}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * TopPicksContent - The original Top Picks functionality
 */
function TopPicksContent() {
  const [picks, setPicks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const { sports: pickSports } = useActiveSports(TOP_PICK_SPORTS);
  const sportFilters = [{ id: 'all', label: 'All Sports' }, ...pickSports];

  // Fetch top picks
  useEffect(() => {
    const fetchTopPicks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append('limit', '30');
        params.append('pick_date', getPicksDate());
        if (selectedSport !== 'all') {
          params.append('sport', selectedSport);
        }

        const response = await api.get(`/top-picks/?${params.toString()}`);
        setPicks(response.data?.picks || []);
      } catch (err) {
        console.error('Error fetching top picks:', err);
        setError(err.response?.data?.detail || 'Failed to load top picks');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopPicks();
  }, [selectedSport]);

  // Filter picks by type
  const filteredPicks = picks.filter(pick => {
    if (selectedType === 'all') return true;
    if (selectedType === 'player_prop') return pick.pick_type === 'player_prop';
    if (selectedType === 'game') return ['game_spread', 'game_total', 'game_ml'].includes(pick.pick_type);
    return true;
  });

  // The featured pick now lives in the Featured Pick Lab (ML + O/U cards) below.
  const regularPicks = filteredPicks.filter(p => !p.is_featured);

  return (
    <div>
      {/* Description */}
      <div className="mb-4">
        <p className="text-text-secondary">
          AI-curated picks based on ML predictions, player trends, and market analysis
        </p>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Sport Filter */}
          <div className="flex-1">
            <label className="block text-xs text-text-secondary mb-2 uppercase tracking-wide">
              Sport
            </label>
            <div className="flex flex-wrap gap-2">
              {sportFilters.map(sport => (
                <button
                  key={sport.id}
                  onClick={() => setSelectedSport(sport.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    selectedSport === sport.id
                      ? 'bg-accent-blue text-white'
                      : 'bg-bg-card hover:bg-bg-card-hover text-text-secondary'
                  }`}
                >
                  {sport.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex-1">
            <label className="block text-xs text-text-secondary mb-2 uppercase tracking-wide">
              Pick Type
            </label>
            <div className="flex flex-wrap gap-2">
              {PICK_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    selectedType === type.id
                      ? 'bg-accent-blue text-white'
                      : 'bg-bg-card hover:bg-bg-card-hover text-text-secondary'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="glass-card p-6 animate-pulse">
              <div className="h-4 bg-bg-card rounded w-1/3 mb-4"></div>
              <div className="h-6 bg-bg-card rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-bg-card rounded w-1/2 mb-4"></div>
              <div className="h-20 bg-bg-card rounded mb-4"></div>
              <div className="h-4 bg-bg-card rounded w-full"></div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="glass-card bg-red-500/10 border-red-500/30 p-6 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-secondary"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredPicks.length === 0 && (
        <div className="glass-card p-8 text-center">
          <div className="text-5xl mb-4">&#128269;</div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No picks available
          </h3>
          <p className="text-text-secondary">
            {selectedSport !== 'all' || selectedType !== 'all'
              ? 'Try adjusting your filters or check back later.'
              : 'Check back later for today\'s picks.'}
          </p>
        </div>
      )}

      {/* Featured Pick Lab — the day's one featured pick from any sport, chosen
          by track record + AI debate (runners/select_featured_pick.py) */}
      <div className="mb-6">
        <FeaturedPickLab sport="all" />
      </div>

      {/* Picks Grid */}
      {!isLoading && !error && regularPicks.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-text-primary mb-3">
            Today's Picks ({regularPicks.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regularPicks.map(pick => (
              <TopPickCard key={pick.id} pick={pick} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Main TopPicks Page Component
 */
export default function TopPicks() {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('top-picks');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check premium status
  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  // Premium gate
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-bg-primary p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="text-6xl mb-4">&#11088;</div>
            <h1 className="text-2xl font-bold text-text-primary mb-4">
              Today's Top Picks
            </h1>
            <p className="text-text-secondary mb-6">
              AI-curated betting picks with detailed analysis and insights.
              Our models analyze player trends, matchups, and market inefficiencies.
            </p>
            <ul className="text-left text-text-secondary mb-8 space-y-2 max-w-sm mx-auto">
              <li className="flex items-center gap-2">
                <span className="text-accent-green">&#10003;</span>
                ML predictions with confidence scores
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">&#10003;</span>
                Vegas edge analysis
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">&#10003;</span>
                Player streak tracking
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">&#10003;</span>
                Head-to-head matchup data
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">&#10003;</span>
                Context signals (home/away, opponent strength)
              </li>
            </ul>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="btn-primary px-6 py-3 text-lg"
            >
              Upgrade to Premium - $30/mo
            </button>
          </div>
        </div>

        <PremiumGateModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title="Unlock Top Picks"
          description="Get daily AI-curated picks with detailed analysis."
          features={[
            "ML predictions with confidence scores",
            "Vegas edge analysis",
            "Player streak tracking (L5, L10)",
            "Head-to-head matchup history",
            "Home/away split analysis",
            "Context signals and risk levels"
          ]}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">&#11088;</span>
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">
              Picks & Edge Finder
            </h1>
          </div>
        </div>

        {/* Main Tab Navigation */}
        <div className="mb-6">
          <TabBar
            tabs={MAIN_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            size="lg"
            variant="primary"
          />
        </div>

        {/* Tab Content */}
        {activeTab === 'top-picks' && <TopPicksContent />}
        {activeTab === 'edges' && <EdgesContent />}
        {activeTab === 'streak' && <StreakContent />}
      </div>
    </div>
  );
}
